import { Button, PauseStackModal } from "./ui";
import { Typography } from "./ui/typography";
import { Loader2, Play, Plus } from "lucide-react";
import zap from "../assets/icons/zap.svg";
import classNames from "classnames";
import React, { useMemo, useState } from "react";
import StatusPill, { StatusPillProps } from "./ui/status-pill";
import pause from "../assets/icons/pause.svg";
import dayjs from "dayjs";
import StringFormatter from "../utils/stringFormatter";

interface StackCardProps {
  id: string;
  icon: string;
  title: string;
  amount: number;
  assetName: string;
  amountText: string;
  frequency: string;
  estimatedTokens: string;
  estimatedValue: string;
  nextPurchase?: string;
  onCreateStack?: () => void;
  isUserStack?: boolean;
  status?: "active" | "inactive" | "paused" | "cancelled";
  onToggleStatus?: () => void;
  isUpdating?: boolean;
  onViewPerformance?: () => void;
  fiatWalletBalance?: number;
  isStatusChanging?: boolean;
}

export default function StackCard({
  id,
  icon,
  title,
  nextPurchase,
  amount,
  assetName,
  amountText,
  frequency,
  estimatedTokens,
  estimatedValue,
  onCreateStack,
  isUserStack = false,
  status = "active",
  onToggleStatus,
  isUpdating = false,
  onViewPerformance,
  fiatWalletBalance = 0,
  isStatusChanging = false,
}: StackCardProps) {
  const [isPauseDialogOpen, setIsPauseDialogOpen] = useState(false);

  const string = new StringFormatter();

  const handlePauseClick = () => {
    if (status === "active") {
      setIsPauseDialogOpen(true);
    } else {
      onToggleStatus?.();
    }
  };

  const handleConfirmPause = () => {
    onToggleStatus?.();
  };

  const shouldShowLoading = isStatusChanging || isUpdating;

  const statusPillVariant = useMemo(() => {
    if (status === "paused") {
      return {
        variant: "paused",
        text: "Paused",
      };
    }

    if (amount > fiatWalletBalance) {
      return {
        variant: "warning",
        text: "Wallet balance low",
      };
    } else {
      return {
        variant: "waiting",
        text: "Awaiting deposit",
      };
    }
  }, [status, amount, fiatWalletBalance]);

  return (
    <div className="border border-bg-neutral rounded-lg pt-5 px-5 bg-white transition-shadow relative">
      {isUserStack && onToggleStatus && (
        <button
          onClick={handlePauseClick}
          disabled={shouldShowLoading}
          className="absolute top-6 right-4 p-2.5 flex items-center justify-center rounded-full border border-gray-200 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={
            shouldShowLoading
              ? "Updating status..."
              : status === "active"
                ? "Pause stack"
                : "Resume stack"
          }
        >
          {shouldShowLoading ? (
            <div className="w-4 h-4  border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
          ) : status === "active" ? (
            <img src={pause} alt="Pause" className="w-3.5 h-3.5" />
          ) : (
            <Play className="w-4 h-4 text-green-600 fill-green-600" />
          )}
        </button>
      )}

      <div className="flex items-start md:items-center flex-col md:flex-row gap-2 mb-4">
        <Typography
          variant="h3"
          className="text-lg font-semibold text-text-primary truncate max-w-[calc(100%-60px)] lg:max-w-[50%] md:max-w-[60%]"
        >
          {title}
        </Typography>
        <div className="flex items-center w-fit gap-1 p-2 rounded-full bg-gray-50">
          <img src={icon} alt={title} className="w-4 h-4" />
          <Typography variant="span" className="text-xs text-text-primary">
            {assetName}
          </Typography>
        </div>
      </div>

      <div className="flex items-center gap-6 mb-6">
        <div className="flex flex-col items-start gap-1">
          <Typography variant="caption" className="text-sm text-text-light">
            Amount
          </Typography>
          <Typography
            variant="caption"
            className="text-sm font-semibold text-text-primary"
          >
            {amountText}
          </Typography>
        </div>

        {isUserStack && (
          <div className="flex flex-col items-start gap-1">
            <Typography variant="caption" className="text-sm text-text-light">
              Frequency
            </Typography>
            <Typography
              variant="caption"
              className="text-sm font-semibold text-text-primary"
            >
              {string.capitalizeFirst(frequency)}
            </Typography>
          </div>
        )}

        <div className="flex flex-col items-start gap-1">
          <Typography variant="caption" className="text-sm text-text-secondary">
            {isUserStack ? "Next purchase" : "Est. accumulation (12 months)"}
          </Typography>
          <Typography variant="caption" className="text-sm text-text-primary">
            {isUserStack && nextPurchase ? (
              <b>{dayjs(nextPurchase).format("DD MMM")}</b>
            ) : (
              <>
                <b>{estimatedTokens}</b> ≈ {estimatedValue}
              </>
            )}
          </Typography>
        </div>
      </div>

      {shouldShowLoading ? (
        <div className="flex items-center mb-2 bg-gray-50 rounded-full px-3 py-2 gap-2 w-fit">
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          <Typography
            variant="span"
            className="text-xs uppercase tracking-wider leading-none"
          >
            Updating status...
          </Typography>
        </div>
      ) : (
        statusPillVariant.variant &&
        statusPillVariant.text && (
          <StatusPill
            variant={statusPillVariant.variant as StatusPillProps["variant"]}
            className="mb-2"
          >
            {statusPillVariant.text}
          </StatusPill>
        )
      )}

      <div className="flex items-center justify-between gap-2 my-4">
        <Button
          className={classNames(
            "bg-transparent rounded-full w-full font-semibold flex items-center justify-center gap-2",
            {
              "text-text-secondary hover:bg-gray-50": !isUserStack,
              "text-accent-pink hover:bg-red-50": isUserStack,
            }
          )}
          onClick={onCreateStack}
        >
          {isUserStack && <Plus className="w-4 h-4 text-accent-pink" />}
          {isUserStack ? "Stack more" : "Create stack"}
          {!isUserStack && <img src={zap} alt="Zap" />}
        </Button>
        {isUserStack && (
          <Button
            className="bg-transparent hover:bg-gray-50 border border-bg-section rounded-full w-full font-semibold flex items-center justify-center gap-2"
            onClick={onViewPerformance}
          >
            <Typography variant="span" className="text-sm text-text-light">
              View Performance
            </Typography>
          </Button>
        )}
      </div>

      <PauseStackModal
        isOpen={isPauseDialogOpen}
        setIsPauseDialogOpen={setIsPauseDialogOpen}
        onConfirmPause={handleConfirmPause}
      />
    </div>
  );
}
